import { useEffect, useRef, type RefObject } from "react";

/**
 * MOBİL GEZİNME ÇEKMECESİNİN KLAVYE SÖZLEŞMESİ (8 Ekim 2026, kullanılabilirlik turu).
 *
 * 1024 pikselin altında ray, kabuğun kendi çizdiği bir çekmecede açılıyor (`AppLayout`). Ölçülen
 * üç kusur: (1) açılınca odak çekmeceye GİRMİYORDU — klavyeyle açan kişi Tab'a basınca arkadaki
 * sayfada geziniyordu; (2) Escape kapatmıyordu; (3) kapanınca odak `<body>`'ye düşüyordu. Kitin
 * `MobileNavScrim`'i yalnız tıklamayı dinliyor ve kitte bu iş için hazır bir sol çekmece yok
 * (`Drawer` sağdan açılan, başlıklı bir kayıt paneli; istek kit oturumuna yazıldı). Sözleşme
 * kabukta, `Drawer`'ın verdiği davranışın aynısı:
 *
 *   - açılınca odak PANELİN KENDİSİNE (arama kutusuna değil: telefonda klavye açılıp listenin
 *     yarısını örterdi — Notlarım'ın çekmecesiyle aynı karar); ilk Tab panelin içine iner;
 *   - Tab ve Shift+Tab panelin içinde döner;
 *   - Escape kapatır — ama bir alt katman (rayın arama kutusundaki terim, açık bir menü) tuşu
 *     zaten kullandıysa (`defaultPrevented`) dokunmaz: bir basış, bir katman;
 *   - kapanınca odak açan kontrole döner, yalnız odak sahipsiz kaldıysa (`<body>`) ya da kapanan
 *     panelin içindeyse. Kişi bu arada başka bir yere odaklandıysa onu oradan çekmiyor.
 */
const TABBABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
  "[contenteditable='true']",
].join(",");

/**
 * Odaklanabilir ve gerçekten ulaşılabilir olanlar — kapalı bir grubun (`inert`) satırları ya da
 * CSS ile gizlenmiş bir düğme değil. `checkVisibility` olmayan ortamda (jsdom) yalnız öznitelikler
 * sorulur.
 */
export function tabbablesIn(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(TABBABLE)).filter(
    (element) =>
      element.closest("[inert], [hidden], [aria-hidden='true']") === null &&
      (typeof element.checkVisibility !== "function" || element.checkVisibility()),
  );
}

export function useModalSheet(input: {
  open: boolean;
  onClose: () => void;
  panel: RefObject<HTMLElement | null>;
}): void {
  const { open, panel } = input;
  const onClose = useRef(input.onClose);
  onClose.current = input.onClose;

  useEffect(() => {
    if (!open) return undefined;
    const node = panel.current;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    node?.focus({ preventScroll: true });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (event.defaultPrevented) return;
        event.preventDefault();
        onClose.current();
        return;
      }
      if (event.key !== "Tab" || node === null) return;
      /**
       * SIRA BURADA HESAPLANIYOR, tarayıcıya bırakılmıyor: kapanmış bir rayın satırları DOM'da
       * duruyor (`inert`) ve sıranın bunları atladığı, bir kenardan öbürüne döndüğü TEK yer
       * burası olsun. Sıra yine belge sırası — yalnız ulaşılamayanlar çıkarılıyor.
       */
      event.preventDefault();
      const items = tabbablesIn(node);
      if (items.length === 0) {
        node.focus({ preventScroll: true });
        return;
      }
      const active = document.activeElement;
      const at = active instanceof HTMLElement ? items.indexOf(active) : -1;
      const next =
        at === -1
          ? event.shiftKey
            ? items.length - 1
            : 0
          : (at + (event.shiftKey ? -1 : 1) + items.length) % items.length;
      items[next]?.focus();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      const active = document.activeElement;
      const orphaned =
        active === null || active === document.body || (node !== null && node.contains(active));
      if (orphaned && opener !== null && opener.isConnected) opener.focus({ preventScroll: true });
    };
  }, [open, panel]);
}
